import type { UIConfigVariable } from 'types/ui-setup-config';
import { paramKey } from 'context/StructureProvider/helper';
import { getCalculationDependencies, type InactiveCalcVariable } from './calculateInactiveInput';

export type EvaluationGroup = { variables: Array<InactiveCalcVariable>; isCycle: boolean };

// Keyed by the variables array; the bridge memoises that array, so the order is computed once per structure.
const orderCache = new WeakMap<
  ReadonlyArray<InactiveCalcVariable>,
  { parameterList: ReadonlyArray<UIConfigVariable>; groups: Array<EvaluationGroup> }
>();

/**
 * Groups the calculations into strongly connected components (Tarjan), emitted
 * dependencies-first. A group with more than one member, or one that reads
 * itself, is a cycle. Everything else is a single calculation ready to evaluate
 * once its group predecessors are done.
 */
export const getEvaluationOrder = (
  inactiveCalcVariables: ReadonlyArray<InactiveCalcVariable>,
  parameterList: ReadonlyArray<UIConfigVariable>,
): Array<EvaluationGroup> => {
  const cached = orderCache.get(inactiveCalcVariables);
  if (cached?.parameterList === parameterList) return cached.groups;

  const keyOf = (variable: InactiveCalcVariable) => paramKey(variable.element, variable.name);
  const byKey = new Map(inactiveCalcVariables.map((variable) => [keyOf(variable), variable]));
  const edges = new Map<string, Array<string>>();
  inactiveCalcVariables.forEach((variable) => {
    const dependencyKeys = getCalculationDependencies(variable, parameterList)
      .map((ref) => paramKey(ref.element, ref.name))
      .filter((key) => byKey.has(key));
    edges.set(keyOf(variable), Array.from(new Set(dependencyKeys)));
  });

  const index = new Map<string, number>();
  const lowLink = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: Array<string> = [];
  const groups: Array<EvaluationGroup> = [];
  let nextIndex = 0;

  const visit = (key: string) => {
    index.set(key, nextIndex);
    lowLink.set(key, nextIndex);
    nextIndex += 1;
    stack.push(key);
    onStack.add(key);

    for (const dependency of edges.get(key) ?? []) {
      if (!index.has(dependency)) {
        visit(dependency);
        lowLink.set(key, Math.min(lowLink.get(key)!, lowLink.get(dependency)!));
      } else if (onStack.has(dependency)) {
        lowLink.set(key, Math.min(lowLink.get(key)!, index.get(dependency)!));
      }
    }

    if (lowLink.get(key) === index.get(key)) {
      const members: Array<string> = [];
      let member: string;
      do {
        member = stack.pop()!;
        onStack.delete(member);
        members.push(member);
      } while (member !== key);
      const isCycle = members.length > 1 || (edges.get(key) ?? []).includes(key);
      groups.push({ variables: members.map((memberKey) => byKey.get(memberKey)!), isCycle });
    }
  };

  inactiveCalcVariables.forEach((variable) => {
    if (!index.has(keyOf(variable))) visit(keyOf(variable));
  });
  orderCache.set(inactiveCalcVariables, { parameterList, groups });
  return groups;
};
